/**
 * Named-app factory. Replaces icespace's single hardcoded `getFirebaseApp()`
 * (`icespace-web/src/config/firebaseConfig.ts`) with a multi-project-aware
 * factory that (a) reuses an already-initialized app *of the same name*, and
 * (b) wires the auth / firestore / rtdb emulator connections from ONE
 * `{host, ports}` config — or the `FIREBASE_EMULATOR_*` env convention — so
 * local + CI runs never touch the live project.
 *
 * This module is the only place in the platform that imports `firebase/app`
 * directly; every other consumer takes the returned handles.
 */
import {
  getApp,
  getApps,
  initializeApp,
  type FirebaseApp,
  type FirebaseOptions,
} from 'firebase/app';
import { connectAuthEmulator, getAuth, type Auth } from 'firebase/auth';
import {
  connectFirestoreEmulator,
  getFirestore,
  type Firestore,
} from 'firebase/firestore';
import {
  connectDatabaseEmulator,
  getDatabase,
  type Database,
} from 'firebase/database';
import { PlatformError } from '@djgg/core';

/** Public web Firebase config (the `firebaseConfig` object from the console). */
export type FirebaseClientConfig = FirebaseOptions;

/**
 * Emulator wiring for local / CI runs. A single `host` plus per-service ports;
 * any omitted port leaves that service pointed at the live backend. Passing an
 * empty object (or `{ host }` with no ports) wires nothing.
 */
export interface EmulatorConfig {
  /** Emulator host (default `127.0.0.1`). */
  host?: string;
  ports?: {
    auth?: number;
    firestore?: number;
    /** Realtime Database emulator port. */
    database?: number;
  };
}

/** The wired Firebase handles a single named app exposes. */
export interface FirebaseHandles {
  app: FirebaseApp;
  auth: Auth;
  firestore: Firestore;
  database: Database;
  /** True when at least one emulator connection was wired on this app. */
  emulated: boolean;
}

export interface CreateFirebaseAppOptions {
  /**
   * Firebase app name. Distinct names let one process hold handles to several
   * projects at once (the platform hosts many games). Defaults to
   * `'[DEFAULT]'` — the same slot icespace's single app used.
   */
  name?: string;
  /**
   * Explicit emulator wiring. When omitted, {@link resolveEmulatorConfig} reads
   * the `FIREBASE_EMULATOR_*` env convention. Pass `false` to force live
   * backends even when the env vars are set.
   */
  emulators?: EmulatorConfig | false;
}

const DEFAULT_APP_NAME = '[DEFAULT]';
const DEFAULT_EMULATOR_HOST = '127.0.0.1';

/**
 * Read the `FIREBASE_EMULATOR_*` env convention into an {@link EmulatorConfig},
 * or `undefined` when no emulator env is set.
 *
 * Recognized (all optional; a service with no port stays on the live backend):
 * - `FIREBASE_EMULATOR_HOST`      — shared host (default `127.0.0.1`)
 * - `FIREBASE_AUTH_EMULATOR_PORT`
 * - `FIRESTORE_EMULATOR_PORT`
 * - `FIREBASE_DATABASE_EMULATOR_PORT`
 *
 * Also honors the Firebase-CLI-native `FIRESTORE_EMULATOR_HOST` /
 * `FIREBASE_AUTH_EMULATOR_HOST` / `FIREBASE_DATABASE_EMULATOR_HOST`
 * (`host:port`) so a `firebase emulators:exec` shell wires with zero extra
 * config. The presence of any of these — or `FIREBASE_EMULATOR_HUB` — enables
 * emulator mode.
 */
export function resolveEmulatorConfig(
  env: Record<string, string | undefined> = readProcessEnv(),
): EmulatorConfig | undefined {
  const host = env.FIREBASE_EMULATOR_HOST || DEFAULT_EMULATOR_HOST;
  const ports: NonNullable<EmulatorConfig['ports']> = {};

  const auth = parsePort(env.FIREBASE_AUTH_EMULATOR_PORT)
    ?? parseHostPort(env.FIREBASE_AUTH_EMULATOR_HOST)?.port;
  const firestore = parsePort(env.FIRESTORE_EMULATOR_PORT)
    ?? parseHostPort(env.FIRESTORE_EMULATOR_HOST)?.port;
  const database = parsePort(env.FIREBASE_DATABASE_EMULATOR_PORT)
    ?? parseHostPort(env.FIREBASE_DATABASE_EMULATOR_HOST)?.port;

  if (auth !== undefined) ports.auth = auth;
  if (firestore !== undefined) ports.firestore = firestore;
  if (database !== undefined) ports.database = database;

  const enabled =
    Boolean(env.FIREBASE_EMULATOR_HUB) ||
    ports.auth !== undefined ||
    ports.firestore !== undefined ||
    ports.database !== undefined;

  if (!enabled) return undefined;

  // A CLI host may override the shared host per service; use the first one seen.
  const cliHost =
    parseHostPort(env.FIRESTORE_EMULATOR_HOST)?.host ||
    parseHostPort(env.FIREBASE_AUTH_EMULATOR_HOST)?.host ||
    parseHostPort(env.FIREBASE_DATABASE_EMULATOR_HOST)?.host;

  return { host: cliHost || host, ports };
}

/**
 * Create (or reuse) a named Firebase app and wire its auth/firestore/rtdb
 * handles, connecting emulators when configured.
 *
 * Reuse rule: if an app with `name` already exists it is returned as-is (matches
 * icespace's `getApps()[0]` reuse) — the handle getters are idempotent, and
 * `connect*Emulator` is only called on a freshly-initialized app, since calling
 * it twice on the same SDK instance throws.
 *
 * @throws {PlatformError} `internal` when initialization fails.
 */
export function createFirebaseApp(
  config: FirebaseClientConfig,
  options: CreateFirebaseAppOptions = {},
): FirebaseHandles {
  const name = options.name ?? DEFAULT_APP_NAME;
  const existing = getApps().some((a) => a.name === name);

  let app: FirebaseApp;
  try {
    app = existing
      ? getApp(name)
      : name === DEFAULT_APP_NAME
        ? initializeApp(config)
        : initializeApp(config, name);
  } catch (e) {
    throw new PlatformError('internal', `firebase app init failed: ${String(e)}`, {
      cause: e,
    });
  }

  const auth = getAuth(app);
  const firestore = getFirestore(app);
  const database = getDatabase(app);

  // Only wire emulators on a *freshly* initialized app — the connect* calls are
  // one-shot per SDK instance and throw if a request already went out.
  let emulated = false;
  if (!existing) {
    const emu =
      options.emulators === false
        ? undefined
        : options.emulators ?? resolveEmulatorConfig();
    if (emu) emulated = wireEmulators({ auth, firestore, database }, emu);
  }

  return { app, auth, firestore, database, emulated };
}

/** Wire the configured emulator connections; returns true if any were wired. */
function wireEmulators(
  handles: { auth: Auth; firestore: Firestore; database: Database },
  emu: EmulatorConfig,
): boolean {
  const host = emu.host || DEFAULT_EMULATOR_HOST;
  const ports = emu.ports ?? {};
  let wired = false;

  if (ports.auth !== undefined) {
    // `disableWarnings` keeps the emulator banner out of test output.
    connectAuthEmulator(handles.auth, `http://${host}:${ports.auth}`, {
      disableWarnings: true,
    });
    wired = true;
  }
  if (ports.firestore !== undefined) {
    connectFirestoreEmulator(handles.firestore, host, ports.firestore);
    wired = true;
  }
  if (ports.database !== undefined) {
    connectDatabaseEmulator(handles.database, host, ports.database);
    wired = true;
  }
  return wired;
}

function readProcessEnv(): Record<string, string | undefined> {
  // `process` may be absent in a pure-browser bundle; guard rather than import
  // @types/node (this package targets both browser and node test runners).
  const g = globalThis as { process?: { env?: Record<string, string | undefined> } };
  return g.process?.env ?? {};
}

function parsePort(v: string | undefined): number | undefined {
  if (v === undefined || v === '') return undefined;
  const n = Number(v);
  return Number.isInteger(n) && n > 0 ? n : undefined;
}

function parseHostPort(v: string | undefined): { host: string; port: number } | undefined {
  if (!v) return undefined;
  const idx = v.lastIndexOf(':');
  if (idx < 0) return undefined;
  const host = v.slice(0, idx) || DEFAULT_EMULATOR_HOST;
  const port = parsePort(v.slice(idx + 1));
  return port === undefined ? undefined : { host, port };
}
